import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { portalService } from "apiServices";
import { Buttons, Displays, Inputs, Modals } from "components";
import { AlertControl } from "components/Modals/Alert";
import {
  BOOKING_STATUS,
  RENDER_BOOKING_SOURCE,
  RENDER_SCHOOL,
  RENDER_SEAT_BOOKING_ERROR,
} from "utils/functions/seatBooking";
import ReasonModal from "../../Modals/ReasonModal";

const ENTRY_OPTIONS = [
  { value: "", label: "ทุกสถานะเข้างาน" },
  { value: "not_entered", label: "ยังไม่เข้างาน" },
  { value: "entered", label: "เข้างานแล้ว" },
];
const METHOD_LABEL = { scan: "สแกน QR", manual: "แอดมินเช็คเอง" };

const formatDate = (value) => (value ? moment(value).format("DD/MM/YYYY HH:mm") : "-");

// แท็บเข้างาน: เฉพาะใบที่จองสำเร็จแล้ว (มี QR) หนึ่งแถวต่อที่นั่ง
// เช็คเข้างานเองได้เมื่อสแกน QR หน้างานไม่ได้ และยกเลิกการเช็คอินได้ (บังคับเหตุผล)
const CheckInTab = ({ activityId, canUpdate }) => {
  const [_isLoading, _setIsLoading] = useState(true);
  const [_isBgLoading, _setIsBgLoading] = useState(false);
  const [_bookings, _setBookings] = useState([]);
  const [_keyword, _setKeyword] = useState("");
  const [_entry, _setEntry] = useState("");
  const [_undoRow, _setUndoRow] = useState(null);

  const _fetchData = useCallback(async () => {
    const res = await portalService.GET_SEAT_BOOKING_LIST(activityId, `?status=${BOOKING_STATUS.APPROVED}`);
    if (res && res.status === 200) _setBookings(res.data.items || []);
    _setIsLoading(false);
  }, [activityId]);

  useEffect(() => {
    _fetchData();
  }, [_fetchData]);

  const rows = useMemo(
    () =>
      _bookings
        .flatMap((b) => b.seats.map((s) => ({ booking: b, seat: s })))
        .sort((a, b) => a.seat.seat_id.localeCompare(b.seat.seat_id, undefined, { numeric: true })),
    [_bookings]
  );

  const shownRows = rows.filter(({ booking, seat }) => {
    if (_entry === "entered" && !seat.used_at) return false;
    if (_entry === "not_entered" && seat.used_at) return false;
    const keyword = _keyword.trim().toLowerCase();
    if (!keyword) return true;
    const booker = booking.booker || {};
    return [seat.seat_id, seat.viewer_name, booking.code, booker.first_name, booker.last_name, booker.phone]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(keyword));
  });

  const enteredCount = rows.filter((r) => r.seat.used_at).length;

  const _callAction = async (request, successMessage) => {
    _setIsBgLoading(true);
    const res = await request();
    _setIsBgLoading(false);
    if (res && res.status === 200) {
      toast.success(successMessage);
      _setUndoRow(null);
      _fetchData();
    } else {
      toast.error(RENDER_SEAT_BOOKING_ERROR(res));
    }
  };

  const _checkIn = ({ booking, seat }) =>
    AlertControl.show({
      title: "เช็คเข้างาน",
      description: `ยืนยันให้ที่นั่ง ${seat.seat_id}${seat.viewer_name ? ` (${seat.viewer_name})` : ""} รหัสจอง ${booking.code} เข้างานใช่หรือไม่`,
      btnLabel1: "ปิด",
      btnLabel2: "เช็คเข้างาน",
      onEvent2: () =>
        _callAction(
          () => portalService.POST_SEAT_BOOKING_CHECKIN(booking._id, { seat_id: seat.seat_id }),
          `เช็คเข้างาน ${seat.seat_id} แล้ว`
        ),
    });

  const _undoCheckIn = (reason) =>
    _callAction(
      () =>
        portalService.POST_SEAT_BOOKING_CHECKIN_UNDO(_undoRow.booking._id, { seat_id: _undoRow.seat.seat_id, reason }),
      `ยกเลิกการเช็คเข้างาน ${_undoRow.seat.seat_id} แล้ว`
    );

  // Export ตามตัวกรองปัจจุบัน หนึ่งแถวต่อที่นั่ง
  const _handleExport = () => {
    const header = [
      "ที่นั่ง", "ชื่อผู้ชม", "รหัสจอง", "ผู้จอง", "เบอร์โทร", "อีเมล", "โรงเรียน", "ประเภท",
      "สถานะเข้างาน", "เวลาเข้างาน", "วิธีเช็คเข้างาน", "แอดมินที่เช็ค",
    ];
    const data = shownRows.map(({ booking, seat }) => {
      const booker = booking.booker || {};
      return [
        seat.seat_id,
        seat.viewer_name || "",
        booking.code,
        `${booker.first_name || ""} ${booker.last_name || ""}`.trim(),
        booker.phone || "",
        booker.email || "",
        RENDER_SCHOOL(booking.school_code, booking.school_name),
        RENDER_BOOKING_SOURCE(booking.booking_source),
        seat.used_at ? "เข้างานแล้ว" : "ยังไม่เข้างาน",
        seat.used_at ? formatDate(seat.used_at) : "",
        seat.used_at ? METHOD_LABEL[seat.checkin_method] || "สแกน QR" : "",
        seat.used_by_admin_name || "",
      ];
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([header, ...data]), "CheckIn");
    XLSX.writeFile(wb, `SeatCheckIn_${moment().format("YYYYMMDD_HHmm")}.xlsx`);
  };

  if (_isLoading) return <Displays.Loading theme_standard_loading heightLayout="360px" />;

  return (
    <div>
      <Displays.BGLoading visible={_isBgLoading} />
      <div className="stat_row">
        <div className="stat_item">
          <div className="stat_label">ที่นั่งจองสำเร็จ</div>
          <div className="stat_value">{rows.length}</div>
        </div>
        <div className="stat_item">
          <div className="stat_label">เข้างานแล้ว</div>
          <div className="stat_value">{enteredCount}</div>
        </div>
        <div className="stat_item">
          <div className="stat_label">ยังไม่เข้างาน</div>
          <div className="stat_value">{rows.length - enteredCount}</div>
        </div>
      </div>

      <div className="filter_row">
        <div className="filter_item">
          <Inputs.Dropdown value={_entry} placeholder={ENTRY_OPTIONS[0].label} options={ENTRY_OPTIONS} onChange={_setEntry} />
        </div>
        <div className="filter_keyword">
          <Inputs.TextField
            value={_keyword}
            placeholder="ค้นหา ที่นั่ง / ชื่อผู้ชม / รหัสจอง / ชื่อ / เบอร์"
            onChange={(e) => _setKeyword(e.target.value)}
          />
        </div>
        <div className="w120">
          <Buttons.BgStandard theme_export_btn label="Export" onClick={_handleExport} />
        </div>
      </div>
      <div className="hint" style={{ marginBottom: 10 }}>
        แสดงเฉพาะใบที่จองสำเร็จแล้ว · ใช้ "เช็คเข้างาน" เมื่อสแกน QR หน้างานไม่ได้ (เช็คได้เฉพาะวันงาน) · กดผิดยกเลิกได้โดยระบุเหตุผล
      </div>

      <div className="table_wrap">
        <table className="seat_table">
          <thead>
            <tr>
              <th>ที่นั่ง</th>
              <th>ชื่อผู้ชม</th>
              <th>รหัสจอง</th>
              <th>ผู้จอง</th>
              <th>โรงเรียน</th>
              <th>สถานะเข้างาน</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {shownRows.length === 0 && (
              <tr>
                <td colSpan={7} className="muted">
                  ไม่มีรายการ
                </td>
              </tr>
            )}
            {shownRows.map((row) => {
              const { booking, seat } = row;
              const booker = booking.booker || {};
              return (
                <tr key={`${booking._id}-${seat.seat_id}`}>
                  <td className="nowrap">{seat.seat_id}</td>
                  <td>{seat.viewer_name || "-"}</td>
                  <td className="nowrap">{booking.code}</td>
                  <td>
                    {`${booker.first_name || ""} ${booker.last_name || ""}`.trim() || "-"}
                    <div className="muted">{booker.phone}</div>
                  </td>
                  <td>{RENDER_SCHOOL(booking.school_code)}</td>
                  <td>
                    {seat.used_at ? (
                      <>
                        <Displays.StatusLabel bg="#33994C" label="เข้างานแล้ว" />
                        <div className="muted">
                          {formatDate(seat.used_at)} · {METHOD_LABEL[seat.checkin_method] || "สแกน QR"}
                          {seat.used_by_admin_name ? ` · ${seat.used_by_admin_name}` : ""}
                        </div>
                      </>
                    ) : (
                      <Displays.StatusLabel bg="#D9D9E0" txtColor="#595966" label="ยังไม่เข้างาน" />
                    )}
                  </td>
                  <td>
                    {canUpdate && (
                      <div className="row_btns">
                        {seat.used_at ? (
                          <div className="w140">
                            <Buttons.BgStandard theme_red label="ยกเลิกเช็คอิน" onClick={() => _setUndoRow(row)} />
                          </div>
                        ) : (
                          <div className="w140">
                            <Buttons.BgStandard theme_green label="เช็คเข้างาน" onClick={() => _checkIn(row)} />
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modals.BodyEmpty isShowModal={!!_undoRow} onCloseModal={() => _setUndoRow(null)}>
        {_undoRow && (
          <ReasonModal
            title={`ยกเลิกการเช็คเข้างาน ${_undoRow.seat.seat_id} (${_undoRow.booking.code})`}
            label="เหตุผล * เช่น กดผิดที่นั่ง"
            required
            confirmLabel="ยกเลิกเช็คอิน"
            onClose={() => _setUndoRow(null)}
            onConfirm={_undoCheckIn}
          />
        )}
      </Modals.BodyEmpty>
    </div>
  );
};

export default CheckInTab;
